import type { UserAPIQueryParams, UserAPIBodyParams } from '~~/entities/api'
import type { ClientDevice, GuestUser, User, UserPermission, UserPermissionFlag } from '~~/entities/user'
import { Role } from '~~/entities/user'
import type { Company } from '~~/entities/company'
import { API_LOGOUT_ENDPOINT, API_REFRESH_ENDPOINT } from '@/composables/useFetchSharedOptions'
import type { ComputedRef, DeepReadonly, Ref } from 'vue'
import type { ClientJS } from 'clientjs'
import { useCompanies, SELECTED_COMPANY_ID_KEY } from '~/composables/useCompanies'
import Intercom, { shutdown as shutdownIntercom } from '@intercom/messenger-js-sdk'
import * as Sentry from '@sentry/vue'
import { ADMIN_HOME_URL } from '~/middleware/redirects.global'
import { Feature } from '~~/entities/feature'

export interface UseUserState {
  clientDevice: ClientDevice
  flags: { [key: string]: boolean }
  followingCompanies: Company[]
  guestUserInfo: GuestUser | undefined | null
  loading: boolean
  token: string
  user: User | undefined
}

export interface UseUser {
  initialize(): Promise<void>
  refreshToken(): Promise<void>
  initializeIntercom(): void
  updateUser(id: number, data: UserAPIBodyParams): Promise<void>
  create(body: { email_address: string; password: string }): Promise<void>
  createWithAlternateService(): Promise<void>
  logout(): Promise<void>
  setGuestUserInfo(guestUserInfo: GuestUser): void
  setFollowCompany(params: { action: 'follow' | 'unfollow'; id: string | number }): Promise<void>
  userRole: ComputedRef<Role | ''>
  userHasCompanies: ComputedRef<boolean>
  doesUserManageCompany(id: number): boolean
  hasRole(...roles: Role[]): boolean
  hasPermission(...permissions: UserPermissionFlag[]): boolean
  hasFlag(...flags: string[]): boolean
  roleCheck(check: Role[], ...roles: { id: number; key: Role; description: string }[]): boolean
  state: DeepReadonly<Ref<UseUserState>>
  onLoginSuccess(): void
  fetchPermissionsForUser(userId: number, companyId: number | string): Promise<void>
  getRoleLabel(role: Role): string
  homepageRoute: ComputedRef<string>
}

export function useUser(): UseUser {
  const config = useRuntimeConfig()
  const { $analytics } = useNuxtApp()
  const router = useRouter()
  const { redirectTo } = useRedirect()

  const initialized = useState('user-initialized', () => false)

  const { selectedCompany, selectCompany } = useCompanies()
  const state = useState<UseUserState>('user', () => ({
    clientDevice: {} as ClientDevice,
    followingCompanies: [],
    loading: true,
    token: '',
    user: undefined,
    guestUserInfo: undefined,
    flags: {},
  }))

  const guestUserInfoCookie = useCookie<object | null>('guest_user_info', { maxAge: 60 * 60 * 24 })

  async function initialize() {
    if (initialized.value) return

    state.value = {
      ...state.value,
      loading: true,
      guestUserInfo: guestUserInfoCookie.value,
    }

    try {
      await _getRefreshToken()
      const user = await $fetch<User>('v2/user', useFetchSharedOptions())
      _setDeviceInfo()

      await _setUserData({ user })
    } catch (e) {
      Sentry.captureException(e)
      _destroy()
    } finally {
      state.value = { ...state.value, loading: false }
      initialized.value = true
    }
  }

  const { updateItem } = useAPIClient<User, UserAPIQueryParams, UserAPIBodyParams>('/v2/users')

  async function updateUser(id: number, data: UserAPIBodyParams): Promise<void> {
    if (state.value.user?.id === id) {
      await updateItem(id, data)

      const user = await $fetch<User>('v2/user', { ...useFetchSharedOptions() })
      await _setUserData({ user })
    }
  }

  async function refreshToken() {
    try {
      await _getRefreshToken()
    } catch {
      await $fetch(API_LOGOUT_ENDPOINT, {
        baseURL: config.public.API_ENDPOINT,
        method: 'POST',
        headers: useRequestHeaders(['cookie']) as HeadersInit,
        credentials: 'include',
      })

      _destroy()

      navigateTo(`/login?redirectedFrom=${window.location.href}`)
    }
  }

  async function create(body: { email_address: string; password: string }) {
    const data = await $fetch<{ access: string; user: User }>('v2/auth', {
      method: 'POST',
      credentials: 'include',
      body,
      ...useFetchSharedOptions(),
    })

    const { access, user } = data || {}

    state.value = { ...state.value, token: access }

    try {
      await _setUserData({ user })

      guestUserInfoCookie.value = null
      state.value = { ...state.value, guestUserInfo: undefined }
    } catch {
      _destroy()
    }
  }

  async function createWithAlternateService(): Promise<void> {
    try {
      await _getRefreshToken()
      const user = await $fetch<User>('v2/user', { ...useFetchSharedOptions() })

      await _setUserData({ user })
    } catch (e) {
      Sentry.captureException(e)
      _destroy()
    }
  }

  const onLoginSuccess = () => {
    const route = useRoute()
    const currentPath = route.path
    const redirectPath = route.query.redirectedFrom as string
    const shouldRedirect = typeof redirectPath === 'string' && !redirectPath.includes('password-reset') && currentPath !== redirectPath
    const shouldRedirectToEditor =
      !redirectPath?.includes('password-reset') && userRole.value && (hasRole(Role.SuperAdmin) || userHasCompanies.value)

    if (shouldRedirect) {
      delete route.query.redirectedFrom

      const resolved = router.resolve(redirectPath)

      if (resolved.matched.length > 0) {
        redirectTo({ path: redirectPath, query: route.query })
      } else {
        window.location.href = redirectPath
      }
      return
    }

    if (shouldRedirectToEditor) {
      navigateTo('/admin')
      return
    }

    navigateTo('/user/profile')
  }

  async function logout(): Promise<void> {
    try {
      await $fetch(API_LOGOUT_ENDPOINT, {
        ...useFetchSharedOptions(),
        method: 'POST',
        headers: useRequestHeaders(['cookie']) as HeadersInit,
        credentials: 'include',
      })
    } finally {
      _destroy()
      window.location.href = '/'
    }
  }

  function setGuestUserInfo(guestUserInfo: GuestUser): void {
    guestUserInfoCookie.value = guestUserInfo
    state.value = { ...state.value, guestUserInfo }
  }

  async function setFollowCompany({ action, id }: { action: 'follow' | 'unfollow'; id: string | number }): Promise<void> {
    await $fetch(`v2/user/companies/${action}/${id}`, {
      method: 'POST',
      ...useFetchSharedOptions(),
    })

    await _fetchFollowingCompanies()
  }

  const userRole = computed(
    () =>
      state.value.user?.roles?.find(({ key }) => [Role.SuperAdmin, Role.CompanyAdmin, Role.AccountLead].includes(key))?.description ?? '',
  )

  const userHasCompanies = computed(() => {
    return !!state.value.user?.companies.length
  })

  function doesUserManageCompany(id: number): boolean {
    if (hasRole(Role.SuperAdmin)) return true

    return !!(state.value.user?.companies.find(c => c.id === id) ?? false)
  }

  function hasRole(...role: Role[]): boolean {
    const userRoles = state.value.user?.roles ?? []
    if (role[0] === Role.InternalAdmin) {
      return roleCheck([Role.SuperAdmin, Role.AccountLead], ...userRoles)
    }

    return roleCheck(role, ...userRoles)
  }

  function roleCheck(check: Role[], ...roles: { id: number; key: Role; description: string }[]): boolean {
    return check.some(role => roles.some(r => r.key === role))
  }

  function hasPermission(...permissions: UserPermissionFlag[]): boolean {
    if (hasRole(Role.InternalAdmin)) return true

    const userPermissions = state.value.user?.permissions || []
    return userPermissions.some(userPermission => permissions.some(permission => userPermission.permission_flag === permission))
  }

  function hasFlag(...flags: string[]) {
    const userFlags = state.value.flags
    return flags.every(flag => userFlags[flag])
  }

  async function fetchPermissionsForUser(userId: number, companyId: number | string) {
    try {
      const resp = await $fetch<{ results: UserPermission[] }>(`/v2/companies/${companyId}/permissions?user_id=${userId}`, {
        ...useFetchSharedOptions(),
        method: 'GET',
      })
      if (state.value.user) {
        state.value.user.permissions = resp.results
      }
    } catch (e) {
      Sentry.captureException(e)
    }
  }

  async function _getRefreshToken(): Promise<void> {
    const response = await $fetch<{ access: string }>(API_REFRESH_ENDPOINT, {
      method: 'POST',
      headers: useRequestHeaders(['cookie']) as HeadersInit,
      credentials: 'include',
      ...useFetchSharedOptions(),
    })

    state.value = { ...state.value, token: response.access }
  }

  async function _setUserData({ user }: { user: User }): Promise<void> {
    state.value = { ...state.value, user: user }
    _resetUserAnalyticsContext()
    let lastUsedCompanyId: string | number | null = localStorage.getItem(SELECTED_COMPANY_ID_KEY)
    const isPrivilegedAdmin = hasRole(Role.InternalAdmin) || hasRole(Role.SuperAdmin)

    // If the last used company is not in the user's companies and the user is not a privileged admin
    // set the last used company to null and remove it from localStorage
    if (lastUsedCompanyId && !isPrivilegedAdmin && !user.companies.find(c => c.id === Number(lastUsedCompanyId))) {
      lastUsedCompanyId = null
      localStorage.removeItem(SELECTED_COMPANY_ID_KEY)
    }

    // Set the last used company to the first company in the array
    if (!lastUsedCompanyId && !isPrivilegedAdmin) {
      lastUsedCompanyId = user.companies[0]?.id
    }

    if (lastUsedCompanyId) {
      // In case company doesn't exist anymore
      try {
        await selectCompany(lastUsedCompanyId)
        await fetchPermissionsForUser(user.id, Number(lastUsedCompanyId))
      } catch (e) {
        Sentry.captureException(e)
      }
    }

    state.value = {
      ...state.value,
      user: {
        ...user,
      },
    }

    const [_, flags] = await Promise.all([
      _fetchFollowingCompanies(),
      $fetch<{ name: string; is_active_for_user: boolean }[]>('v2/flags', useFetchSharedOptions()),
    ])

    state.value = {
      ...state.value,
      flags: flags.reduce<{ [key: string]: boolean }>((acc, { name, is_active_for_user }) => {
        acc[name] = is_active_for_user
        return acc
      }, {}),
    }
  }

  async function _fetchFollowingCompanies(): Promise<void> {
    const data = await $fetch<{ results: { company: Company }[] }>('v2/user/companies/following', useFetchSharedOptions())

    state.value = {
      ...state.value,
      followingCompanies: data.results.map(({ company }: { company: Company }) => company),
    }
  }

  function _setDeviceInfo() {
    // state.value.clientDevice.fingerprint = $clientJS.getFingerprint().toString() || Date.now()
    // state.value.clientDevice.browser = $clientJS.getBrowser() || null
    // state.value.clientDevice.browserVersion = $clientJS.getBrowserMajorVersion() || null
    // state.value.clientDevice.os = _getClientOS($clientJS)
    // state.value.clientDevice.osVersion = $clientJS.getOSVersion() || null
    // state.value.clientDevice.isMobile = $clientJS.isMobile()
    // state.value.clientDevice.isMobileIOS = $clientJS.isMobileIOS()
    // state.value.clientDevice.device = $clientJS.getDevice() || null
  }

  function _getClientOS(client: ClientJS) {
    const os = client.getOS()

    if (client.isMac() || os === 'iOS') {
      return 'mac'
    }
    if (client.isWindows() || os === 'Android') {
      return 'windows'
    }
    if (client.isLinux()) {
      return 'linux'
    }

    return ''
  }

  function _destroy(): void {
    state.value = {
      ...state.value,
      user: undefined,
      token: '',
      followingCompanies: [],
      flags: {},
    }

    // $analytics?.unidentify()

    // $sentry.configureScope(scope => {
    //   scope.clear()
    // })
    if (import.meta.client) {
      shutdownIntercom()
    }
  }

  function _resetUserAnalyticsContext() {
    if (state.value.user) {
      $analytics?.identify({
        id: `${state.value.user.id}`,
        name: state.value.user.name ?? '',
        emailAddress: state.value.user.email_address ?? '',
        profileImage: state.value.user.profile_image ?? '',
        // linkedIn: state.value.user.linkedin_id ?? '',
        // isSuperAdmin: state.value.user.is_super_admin ?? '',
        // userRole: userRole.value,
        // companyName: selectedCompany.value?.name ?? '',
        // companies: state.value.user.companies?.map(u => u?.id) ?? [],
        // planType: selectedCompany.value?.payment_plan?.plan_tier ?? '',
      })
      // $sentry.configureScope(scope => {
      //   if (state.value.user?.email_address) {
      //     scope.setUser({ email: state.value.user.email_address })
      //   }
      // })
    }

    initializeIntercom()
  }

  function initializeIntercom() {
    if (!import.meta.client) {
      return
    }

    const intercomConfig: any = {
      app_id: config.public.VUE_APP_INTERCOM_APP_ID,
      custom_launcher_selector: '#intercom-trigger',
      hide_default_launcher: true,
      alignment: 'right',
    }

    if (!window.Intercom) {
      Intercom(intercomConfig)
    } else {
      // Shutdown the old instance of the intercom stuff
      shutdownIntercom()
    }

    const { user } = state.value
    if (user) {
      Object.assign(intercomConfig, {
        user_type: userRole.value,
        user_id: user.id,
        name: user.name,
        email: user.email_address,
      })
    }

    if (selectedCompany.value) {
      Object.assign(intercomConfig, {
        company: {
          company_id: selectedCompany.value.id,
          name: selectedCompany.value.name,
          plan: selectedCompany.value.payment_plan?.plan_tier,
        },
      })
    }

    if (window.Intercom) {
      window.Intercom('reattach_activator')
      window.Intercom('boot', intercomConfig)
    }
  }

  function getRoleLabel(role: Role): string {
    const labels = {
      [Role.SuperAdmin]: 'VRIFY Super Admin',
      [Role.AccountLead]: 'VRIFY Employee',
      [Role.CompanyAdmin]: 'Client User',
      [Role.InternalAdmin]: 'Internal Admin',
    }
    return labels[role] ?? ''
  }

  const homepageRoute = computed<string>(() => {
    if (hasFlag(Feature.AdminHomepageEnabled)) {
      return '/'
    }
    return ADMIN_HOME_URL
  })

  return {
    initialize,
    refreshToken,
    create,
    createWithAlternateService,
    logout,
    setGuestUserInfo,
    setFollowCompany,
    userRole,
    userHasCompanies,
    fetchPermissionsForUser,
    doesUserManageCompany,
    hasRole,
    hasPermission,
    hasFlag,
    roleCheck,
    initializeIntercom,
    updateUser,
    onLoginSuccess,
    state: readonly(state),
    getRoleLabel,
    homepageRoute,
  }
}
